import { insightActionsApi } from '../api/InsightActionsApi'
import { ApplyActionsFlow } from '../flows/ApplyActionsFlow'
import { LoadPreviewFlow } from '../flows/LoadPreviewFlow'
import { PollRunFlow } from '../flows/PollRunFlow'
import { listedRows } from '../service/insightActionSelection'
import { newIdempotencyKey } from '../service/idempotencyKey'
import { useInsightActionStore } from '../stores/insightActionStore'

import { useInsightActionScope } from './useInsightActionScope'

export interface UseInsightActionCommands {
  refreshPreview: () => Promise<void>
  enterConfirm: () => void
  apply: () => Promise<void>
  recoverFromStale: () => Promise<void>
  retryRows: (rowKeys: string[]) => Promise<void>
}

export function useInsightActionCommands(): UseInsightActionCommands {
  const store = useInsightActionStore()
  const scope = useInsightActionScope()
  const loadPreview = new LoadPreviewFlow({ api: insightActionsApi, store })
  const applyActions = new ApplyActionsFlow({ api: insightActionsApi, store })
  const pollRun = new PollRunFlow({ api: insightActionsApi, store })

  async function refreshPreview(): Promise<void> {
    const ids = scope.forInsight(store.insightId)
    if (!ids) return
    await loadPreview.execute(ids, { force: true })
  }

  async function apply(): Promise<void> {
    const ids = scope.forInsight(store.insightId)
    const runScope = scope.forRun()
    if (!ids || !runScope) return

    const result = await applyActions.execute(ids)
    if (!result) return
    store.setStep('progress')
    void pollRun.execute(runScope, store.activeKey)
  }

  return {
    refreshPreview,
    enterConfirm: () => {
      store.setIdempotencyKey(newIdempotencyKey())
      store.setStep('confirm')
    },
    apply,
    recoverFromStale: async () => {
      const wanted = [...store.selection]
      await refreshPreview()
      const alive = new Set(
        listedRows(store.activeGroup, store.groups.length).map((row) => row.rowKey),
      )
      const kept = wanted.filter((key) => alive.has(key))
      store.setSelection(kept)
      store.setStaleRecovery({ kept: kept.length, dropped: wanted.length - kept.length })
      store.setApplyError('')
      store.setStep('select')
    },
    retryRows: async (rowKeys) => {
      if (!rowKeys.length) return
      await refreshPreview()
      const alive = new Set(
        listedRows(store.activeGroup, store.groups.length).map((row) => row.rowKey),
      )
      store.setSelection(rowKeys.filter((key) => alive.has(key)))
      store.setIdempotencyKey(newIdempotencyKey())
      store.dropRun(store.activeKey)
      store.setStep('progress')
      await apply()
    },
  }
}
